import { DateTime } from "luxon";
import { add, flatMap, map, uniq, takeWhile, takeRightWhile, pull, find } from "../../utils/lodash";
import { QuestionnaireModel } from "../QuestionnaireModel";
import { getSettingString, getSettingEnum, getSettingBoolean } from "./settingUtilities";
import { DispositionCode } from "../enums/DispositionCode";
import { TerminationAction } from "../enums/TerminationAction";
import { isNullOrEmpty } from "../../utils";
import { BlockModel } from "../BlockModel";
import { AutoAdvanceMode } from "../enums/AutoAdvanceMode";
import { translate } from "./translationUtilities";
import { TerminationOptionsModel } from "../TerminationOptionsModel";
import { QuestionType } from "../enums/QuestionType";

export function getDefaultLanguage(questionnaire: QuestionnaireModel) {
    return questionnaire.defaultLanguage;
}

export const shouldExecuteResumeActions = (questionnaire: QuestionnaireModel) =>
    getSettingBoolean(questionnaire, "EXECUTE_ADVANCED_ACTIONS_ON_RESUME");

export function isAutoAdvanceEnabled(questionnaire: QuestionnaireModel, block: BlockModel) {
    const autoAdvanceMode = getSettingEnum(questionnaire, AutoAdvanceMode, "AUTO_ADVANCE_MODE");
    if (autoAdvanceMode === AutoAdvanceMode.ByBlock) {
        return getSettingBoolean(block, "AutoAdvance");
    }
    return autoAdvanceMode === AutoAdvanceMode.Enabled;
}

export function isExpired(questionnaire: QuestionnaireModel) {
    const startDateString = getSettingString(questionnaire, "SURVEY_START_DATE");
    const endDateString = getSettingString(questionnaire, "SURVEY_END_DATE");
    const dateFormat = "MM/DD/YYYY";
    if (startDateString) {
        const startDate = DateTime.fromFormat(startDateString, dateFormat).toUTC();
        if (startDate && DateTime.utc() < startDate) {
            return true;
        }
    }
    if (endDateString) {
        const endDate = DateTime.fromFormat(endDateString, dateFormat).toUTC();
        if (endDate && DateTime.utc() < endDate) {
            return true;
        }
    }
    return false;
}

export const getTerminationOptions = (
    questionnaire: QuestionnaireModel,
    language: string,
    status?: DispositionCode,
): TerminationOptionsModel => {
    if (DispositionCode[status]) {
        let terminationType = DispositionCode[status];
        let terminationAction = getSettingEnum(
            questionnaire,
            TerminationAction,
            `${terminationType.toUpperCase()}_ACTION`,
        );
        while (true) {
            if (terminationAction === TerminationAction.RedirectToUrl) {
                const url = getSettingString(
                    questionnaire,
                    `${terminationType.toUpperCase() || "TERMINATION"}_ACTION_REDIRECT_URL`,
                );
                if (!isNullOrEmpty(url)) {
                    return { redirectUrl: url };
                }
                return {};
            }
            if (terminationAction === TerminationAction.DisplayMessage) {
                const message = translate(
                    questionnaire,
                    `${terminationType}TerminationMessage`,
                    language,
                );
                if (!isNullOrEmpty(message)) {
                    return { message: message };
                }
                return {};
            }
            if (terminationType === "") {
                return {};
            }
            terminationType = "";
            terminationAction = getSettingEnum(
                questionnaire,
                TerminationAction,
                "TERMINATION_ACTION",
            );
        }
    }
    return {};
};
export const getTotalQuestions = (questionnaire: QuestionnaireModel) =>
    questionnaire.blocks.map((b) => b.questions.length).reduce(add);

export const getBlock = (questionnaire: QuestionnaireModel, name: string) =>
    questionnaire.blocks.find((b) => b.name.toUpperCase() === name.toUpperCase());

export const getBlockWithVariable = (questionnaire: QuestionnaireModel, variable: string) => {
    const question = getQuestionWithVariable(questionnaire, variable);
    if (question == null) {
        return undefined;
    }
    return questionnaire.blocks[question.blockIndex];
};

export const getBlockWithQuestion = (questionnaire: QuestionnaireModel, questionName: string) => {
    const question = getQuestionWithName(questionnaire, questionName);
    if (question == null) {
        return undefined;
    }
    return questionnaire.blocks[question.blockIndex];
};

export const getQuestionWithName = (questionnaire: QuestionnaireModel, name: string) => {
    if (questionnaire == null || isNullOrEmpty(name)) {
        return undefined;
    }

    const questionName = name.toUpperCase();
    return questionnaire.blocks
        .flatMap((block) => block.questions)
        .find((question) => question.name.toUpperCase() === questionName);
};

export const getQuestionWithId = (questionnaire: QuestionnaireModel, id: number) => {
    if (questionnaire == null || !id) {
        return undefined;
    }

    return questionnaire.blocks
        .flatMap((block) => block.questions)
        .find((question) => question.id === id);
};

export const getVariableWithName = (questionnaire: QuestionnaireModel, name: string) => {
    if (questionnaire == null || isNullOrEmpty(name)) {
        return undefined;
    }
    const variableName = name.toUpperCase();
    return questionnaire.blocks
        .flatMap((b) => b.questions.flatMap((q) => q.variables))
        .find((v) => v.name.toUpperCase() === variableName);
};

export const getQuestionTypeByVariableName = (
    questionnaire: QuestionnaireModel,
    name: string,
): QuestionType => {
    if (questionnaire == null || isNullOrEmpty(name)) {
        return undefined;
    }
    return getQuestionWithVariable(questionnaire, name)?.type;
};

export const getQuestionWithVariable = (questionnaire: QuestionnaireModel, name: string) => {
    if (questionnaire == null || isNullOrEmpty(name)) {
        return undefined;
    }
    const variableName = name.toUpperCase();
    return questionnaire.blocks
        .flatMap((b) => b.questions)
        .find((q) => q.variables.find((v) => v.name.toUpperCase() === variableName));
};

export const getVariableRange = (
    questionnaire: QuestionnaireModel,
    start: string,
    end: string,
    includeFirst = true,
) => {
    if (questionnaire == null) {
        return undefined;
    }
    const allQuestions = flatMap(questionnaire.blocks, (b) => map(b.questions, (q) => q));
    if (end.startsWith("+")) {
        const startQuestion = allQuestions.find((q) => q.name === start);
        const endQuestionIndex = allQuestions.indexOf(startQuestion) + Number(end) - 1;
        end = allQuestions[endQuestionIndex].name;
    }
    const questionsAndVariablesNames = uniq(
        flatMap(allQuestions, (q) => [q.name, ...q.variableNames]),
    );
    // Get variables to remove
    const left = takeWhile(
        questionsAndVariablesNames,
        (v) => v.toUpperCase() !== start.toUpperCase(),
    );
    const right = takeRightWhile(
        questionsAndVariablesNames,
        (v) => v.toUpperCase() !== end.toUpperCase(),
    );
    // Remove variables outside of the range
    pull(questionsAndVariablesNames, ...left, ...right);

    // Remove first element
    if (!includeFirst) {
        const first = questionsAndVariablesNames.shift();
        const question = allQuestions.find((q) => q.name === first);

        // If it is a question removes its variables too
        if (question) {
            pull(questionsAndVariablesNames, ...question.variableNames);
        }
    }

    const allVariables = flatMap(allQuestions, (q) => q.variables);
    return uniq(
        flatMap(questionsAndVariablesNames, (name) => {
            // Return the variable or all the question variables depending on the case
            return (
                find(allVariables, (v) => v.name === name) ||
                find(allQuestions, (q) => q.name === name).variables
            );
        }),
    );
};

export const getCompletionQuestion = (questionnaire: QuestionnaireModel) => {
    const questionName = getSettingString(questionnaire, "COMPLETION_QUESTION");
    if (isNullOrEmpty(questionName)) {
        return null;
    }
    return getQuestionWithName(questionnaire, questionName);
};

export const isInputRequired = (questionnaire: QuestionnaireModel) =>
    getSettingBoolean(questionnaire, "INPUT_REQUIRED");
